/**
 * Parse an axios/fetch error into a user-friendly message for auth flows.
 * Distinguishes server downtime, network outages, timeouts, validation errors,
 * and known auth failures so the toast actually tells the user what to do.
 */
export function getAuthErrorMessage(error, fallback = "Authentication failed") {
  if (!error) return fallback;

  // Backend returned a structured error (most reliable signal)
  const detail = error.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;

  const status = error.response?.status;
  const code = error.code;

  // Backend reachable but errored — map common HTTP codes
  if (status === 401) return "Incorrect email or password.";
  if (status === 403) return "Access denied. Please try again.";
  if (status === 404) return "Account not found. Please check your email.";
  if (status === 409) return "An account with this email already exists.";
  if (status === 422) return "Please check your details and try again.";
  if (status === 429) return "Too many attempts. Please wait a moment and try again.";
  if (status >= 500) return "Server error. Our team has been notified — please try again shortly.";

  // No response from server — figure out why
  const msg = (error.message || "").toLowerCase();
  if (code === "ECONNABORTED" || msg.includes("timeout")) {
    return "Server is taking too long to respond. Please try again in a moment.";
  }
  if (code === "ERR_NETWORK" || msg.includes("network error")) {
    return "Server unavailable. Please check your connection and try again.";
  }
  if (msg.includes("failed to fetch")) {
    return "Server unavailable. Please try again shortly.";
  }

  return fallback;
}
